import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Slider,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  useMatchEventsMatches,
  useActivateMatch,
  useDeactivateMatch,
  useUpsertMatch,
  useMatchEventsHistory,
} from '../../api/useMatchEvents';
import { useMatchEventsSocket } from '../../hooks/useMatchEventsSocket';
import {
  buildMatchEventRows,
  type MatchEventRow as MatchEventRowVM,
} from '../../utils/matchEventsFeed';
import { getCategoryDisplay } from './category-display';
import {
  SPORT_IDS,
  categoriesForSport,
  defaultCategoriesForSport,
  type MatchEventCategory,
  type MatchEventsFilter,
} from '../../types/matchEvents.types';
import {
  readMatchEventsFilterFromParams,
  writeMatchEventsFilterToParams,
  deriveSummary,
  isBaseballSummary,
  mergeHistoryAndLive,
  deriveAnnulledSide,
  derivePeriodMarkers,
  type MetricSummary,
} from '../../utils/matchEventsFeed';
import { useMatchEventSound } from '../../hooks/useMatchEventSound';

/** "1st Half" → "1H"; falls back to the raw name. */
function shortPeriod(name: string): string {
  const m = name.match(/(\d)/);
  return m ? `${m[1]}H` : name;
}

/** Per-half breakdown, e.g. "1H 2–0 · 2H 0–2". */
function formatHalfBreakdown(metric: MetricSummary): string {
  return metric.byHalf
    .map((h) => `${shortPeriod(h.periodName)} ${h.home}–${h.away}`)
    .join(' · ');
}

/**
 * Flatten the raw `Body` into label/value pairs for the expanded panel.
 * "All fields" per the contract — nothing is dropped: nulls render as `null`,
 * primitive arrays list their values, and objects deeper than the recursion
 * limit fall back to JSON instead of `[object Object]`.
 */
function flattenRaw(
  obj: Record<string, unknown>,
  prefix = '',
  depth = 0,
): Array<[string, string]> {
  const MAX_DEPTH = 3;
  const out: Array<[string, string]> = [];
  for (const [k, v] of Object.entries(obj)) {
    const label = prefix ? `${prefix} → ${k}` : k;
    if (v === null || v === undefined) {
      out.push([label, v === null ? 'null' : 'undefined']);
    } else if (Array.isArray(v)) {
      const isObjectArray =
        v.length > 0 && typeof v[0] === 'object' && v[0] !== null;
      if (v.length === 0) {
        out.push([label, '[]']);
      } else if (isObjectArray && depth < MAX_DEPTH) {
        v.forEach((item, idx) =>
          out.push(
            ...flattenRaw(
              item as Record<string, unknown>,
              `${label} [${idx + 1}]`,
              depth + 1,
            ),
          ),
        );
      } else {
        out.push([
          label,
          v
            .map((x) => (typeof x === 'object' ? JSON.stringify(x) : String(x)))
            .join(', '),
        ]);
      }
    } else if (typeof v === 'object' && depth < MAX_DEPTH) {
      out.push(...flattenRaw(v as Record<string, unknown>, label, depth + 1));
    } else if (typeof v === 'object') {
      out.push([label, JSON.stringify(v)]);
    } else {
      out.push([label, String(v)]);
    }
  }
  return out;
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString();
  } catch {
    return iso;
  }
}

/** Confidence % → signal colour: 100 = confirmed (green), mid = amber, low = grey. */
function confidenceColor(pct: number): string {
  if (pct >= 100) return '#4caf50';
  if (pct >= 50) return '#ffb300';
  return '#9e9e9e';
}

/** Saturated per-side colours so the team stands out at a glance. */
const HOME_COLOR = '#5c93c4';
const AWAY_COLOR = '#c17a58';

/**
 * One match-event row — category label+emoji, a prominent confidence chip up
 * front, name, the team (strongly coloured), value home:away, game clock,
 * period, receivedAt. Click anywhere on the header toggles the expanded panel
 * that dumps ALL raw `Body` fields (mirrors `NotificationRow`).
 */
const MatchEventRow = memo(function MatchEventRow({
  row,
  homeTeam,
  awayTeam,
}: {
  row: MatchEventRowVM;
  homeTeam?: string;
  awayTeam?: string;
}) {
  const [open, setOpen] = useState(false);
  const { display, frame } = row;
  const homeLabel = homeTeam ?? 'Home';
  const awayLabel = awayTeam ?? 'Away';
  // A team-level home:away reading (goals/corners/team stats) → label BOTH
  // sides by name; otherwise a single side/value.
  const isHomeAway = frame.homeValue != null && frame.awayValue != null;
  return (
    <Paper
      variant="outlined"
      sx={{
        py: 0.5,
        pl: 1.5,
        pr: 1.5,
        mb: 0.5,
        borderLeft: 3,
        borderLeftColor: display.color,
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        onClick={() => setOpen((o) => !o)}
        sx={{ cursor: 'pointer', minHeight: 28 }}
      >
        <Box
          aria-hidden
          sx={{ fontSize: '1rem', lineHeight: 1, minWidth: 22, textAlign: 'center' }}
        >
          {display.emoji}
        </Box>
        <Chip
          size="small"
          label={display.label}
          variant="outlined"
          sx={{
            borderColor: display.color,
            color: display.color,
            fontWeight: 500,
            height: 20,
            fontSize: '0.7rem',
            '& .MuiChip-label': { px: 0.75 },
          }}
        />
        {/* Confidence — prominent + up front (100% = confirmed). */}
        <Box
          sx={{
            minWidth: 46,
            textAlign: 'center',
            px: 0.75,
            py: '2px',
            borderRadius: 1,
            fontWeight: 700,
            fontSize: '0.8rem',
            color: '#000',
            bgcolor: confidenceColor(row.confidencePct),
          }}
        >
          {row.confidencePct}%
        </Box>
        <Typography
          variant="body2"
          sx={{
            color: row.annulled ? 'text.disabled' : 'common.white',
            fontWeight: 500,
            textDecoration: row.annulled ? 'line-through' : 'none',
          }}
        >
          {row.name}
        </Typography>
        {row.annulled && (
          <Chip
            size="small"
            label="⚠️ ANULADO"
            sx={{
              height: 20,
              fontSize: '0.7rem',
              fontWeight: 700,
              color: '#fff',
              bgcolor: '#d32f2f',
              '& .MuiChip-label': { px: 0.8 },
            }}
          />
        )}
        {row.team && (
          <Chip
            size="small"
            label={row.team === 'home' ? homeLabel : awayLabel}
            sx={{
              height: 20,
              fontSize: '0.7rem',
              fontWeight: 700,
              color: '#fff',
              bgcolor: row.team === 'home' ? HOME_COLOR : AWAY_COLOR,
              '& .MuiChip-label': { px: 0.8 },
            }}
          />
        )}
        {row.playerName && (
          <Typography variant="caption" color="text.secondary">
            {row.playerName}
          </Typography>
        )}
        {isHomeAway ? (
          // Team-level home:away → both teams by name, each side colour-coded.
          <Chip
            size="small"
            variant="outlined"
            label={
              <span>
                <span style={{ color: HOME_COLOR, fontWeight: 700 }}>
                  {homeLabel} {frame.homeValue}
                </span>
                {' – '}
                <span style={{ color: AWAY_COLOR, fontWeight: 700 }}>
                  {frame.awayValue} {awayLabel}
                </span>
              </span>
            }
            sx={{
              height: 20,
              fontSize: '0.7rem',
              textDecoration: row.annulled ? 'line-through' : 'none',
              opacity: row.annulled ? 0.6 : 1,
              '& .MuiChip-label': { px: 0.8 },
            }}
          />
        ) : (
          row.value != null && (
            <Chip
              size="small"
              label={row.value}
              variant="outlined"
              sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.6 } }}
            />
          )
        )}
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {row.clock}
          {row.periodName ? ` · ${row.periodName}` : ''}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {formatTime(row.receivedAt)}
        </Typography>
      </Stack>
      {open && (
        <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'background.default', borderRadius: 1 }}>
          <Stack spacing={0.5}>
            {flattenRaw(frame.raw).map(([label, value]) => (
              <Stack key={label} direction="row" spacing={2} alignItems="flex-start">
                <Typography
                  variant="caption"
                  sx={{ minWidth: 200, color: 'text.secondary', flexShrink: 0 }}
                >
                  {label}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: 'common.white', wordBreak: 'break-word', flex: 1 }}
                >
                  {value}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      )}
    </Paper>
  );
});

/**
 * The Live Match Events tab: pick a match, activate/deactivate its live
 * feed, and stream real-time incidents for the selected (active) fixture.
 * Filters (categories / min-confidence / show-all / raw) are persisted to
 * URL `ev*` params (the `tab` param is preserved so the notifications tab
 * URL survives).
 */
export function LiveMatchEventsTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState<MatchEventsFilter>(() =>
    readMatchEventsFilterFromParams(searchParams),
  );

  // Persist filter to URL, preserving every non-`ev*` param (esp. `tab`).
  useEffect(() => {
    setSearchParams(
      (prev) => writeMatchEventsFilterToParams(filter, new URLSearchParams(prev)),
      { replace: true },
    );
  }, [filter, setSearchParams]);

  const { data: matches = [], isLoading, isError } = useMatchEventsMatches();
  const activate = useActivateMatch();
  const deactivate = useDeactivateMatch();
  const upsert = useUpsertMatch();

  // "Add match by fixture ID" form — the operator registers an LSports
  // Hyper-Livescore fixture (id + team names) so it appears in the picker and
  // can be activated. Upsert on fixtureId (backend POST /match-events/matches).
  const [newMatch, setNewMatch] = useState<{
    fixtureId: string;
    homeTeam: string;
    awayTeam: string;
    league: string;
    sport: 'auto' | 'soccer' | 'baseball';
  }>({
    fixtureId: '',
    homeTeam: '',
    awayTeam: '',
    league: '',
    sport: 'auto',
  });
  const newFixtureIdNum = Number(newMatch.fixtureId);
  const canAddMatch =
    Number.isInteger(newFixtureIdNum) &&
    newFixtureIdNum > 0 &&
    newMatch.homeTeam.trim().length > 0 &&
    newMatch.awayTeam.trim().length > 0;

  const addMatch = useCallback(() => {
    if (!canAddMatch) return;
    // Auto → omit sportId so the backend stores null (provisional) and resolves
    // it from the feed header; a concrete pick is stored as-is.
    const sportId =
      newMatch.sport === 'soccer'
        ? SPORT_IDS.SOCCER
        : newMatch.sport === 'baseball'
          ? SPORT_IDS.BASEBALL
          : undefined;
    upsert.mutate(
      {
        fixtureId: newFixtureIdNum,
        homeTeam: newMatch.homeTeam.trim(),
        awayTeam: newMatch.awayTeam.trim(),
        league: newMatch.league.trim() || null,
        ...(sportId != null ? { sportId } : {}),
      },
      {
        onSuccess: () => {
          // Auto-select the freshly-registered match in the picker.
          setFilter((f) => ({ ...f, fixtureId: newFixtureIdNum }));
          setNewMatch({
            fixtureId: '',
            homeTeam: '',
            awayTeam: '',
            league: '',
            sport: 'auto',
          });
        },
      },
    );
  }, [canAddMatch, newFixtureIdNum, newMatch, upsert]);

  const selectedMatch = useMemo(
    () => matches.find((m) => m.fixtureId === filter.fixtureId) ?? null,
    [matches, filter.fixtureId],
  );

  // Only an ACTIVE match streams — subscribing to an inactive fixture
  // would join a room with no feed attached.
  const subscribeFixtureId =
    selectedMatch && selectedMatch.active ? selectedMatch.fixtureId : null;

  const { isConnected, frames: liveFrames } = useMatchEventsSocket({
    fixtureId: subscribeFixtureId,
    includeRaw: filter.raw,
  });

  // Durable history (persisted match_events, 24h TTL) — rebuilds the FULL feed
  // after a refresh instead of only the ~20-min live replay window. Merged with
  // the live buffer (de-duped by streamId), so nothing disappears mid-match.
  const { data: history = [], isFetched: historyFetched } =
    useMatchEventsHistory(subscribeFixtureId);
  const frames = useMemo(
    () => mergeHistoryAndLive(history, liveFrames),
    [history, liveFrames],
  );

  // Recomputes as the feed grows and whenever the filter changes.
  const rows = useMemo(
    () => buildMatchEventRows(frames, filter),
    [frames, filter],
  );

  // "End of half/period" dividers, synthesised from the period each frame
  // carries. Always shown (independent of the category filter) and merged into
  // the feed chronologically.
  const periodMarkers = useMemo(() => derivePeriodMarkers(frames), [frames]);
  const feedItems = useMemo(() => {
    const items: Array<
      | { type: 'row'; key: string; receivedAt: string; row: (typeof rows)[number] }
      | {
          type: 'marker';
          key: string;
          receivedAt: string;
          label: string;
        }
    > = [
      ...rows.map((r) => ({
        type: 'row' as const,
        key: r.frame.streamId || r.collapseKey,
        receivedAt: r.receivedAt,
        row: r,
      })),
      ...periodMarkers.map((m) => ({
        type: 'marker' as const,
        key: m.key,
        receivedAt: m.receivedAt,
        label: m.label,
      })),
    ];
    items.sort(
      (a, b) =>
        new Date(b.receivedAt).getTime() - new Date(a.receivedAt).getTime(),
    );
    return items;
  }, [rows, periodMarkers]);

  // Resolve the fixture's sport: the stored pick wins; else fall back to the
  // first frame carrying a resolved `sportId`. null until either resolves →
  // neutral (full-union) filter + soccer scoreboard.
  const frameSportId = useMemo(() => {
    for (const f of frames) if (f.sportId != null) return f.sportId;
    return null;
  }, [frames]);
  // Prefer the LIVE feed's sport (authoritative — it's what the backend
  // classifies incidents by) over the stored pick; the stored value only seeds
  // the view before any frame arrives. A stored manual sport that disagrees
  // with the feed therefore never blanks the view — it just surfaces a hint.
  const sport = frameSportId ?? selectedMatch?.sportId;
  const isBaseball = sport === SPORT_IDS.BASEBALL;
  const sportMismatch =
    frameSportId != null &&
    selectedMatch?.sportId != null &&
    frameSportId !== selectedMatch.sportId;

  // Filter vocabulary for the current sport (full union until a sport resolves).
  const sportCategories = useMemo(() => categoriesForSport(sport), [sport]);

  // Sport-aware scoreboard: baseball → running runs + inning; else the soccer
  // goals/corners tally straight off the latest authoritative frames.
  const summary = useMemo(() => deriveSummary(frames, sport), [frames, sport]);
  const soccerSummary = isBaseballSummary(summary) ? null : summary;
  const baseballSummary = isBaseballSummary(summary) ? summary : null;

  // Audible cue on a NEW primary/secondary tally increase (goal-run / corner-hr).
  // Toggleable. `ready` waits for the history load so the initial 0→N jump
  // never fires a cue.
  const [soundsEnabled, setSoundsEnabled] = useState(true);
  useMatchEventSound(summary, {
    enabled: soundsEnabled,
    fixtureId: subscribeFixtureId,
    ready: subscribeFixtureId == null || historyFetched,
  });

  // Disallowed-goal detector (soccer only): LSports flags a VAR-annulled goal
  // with an explicit `Score` incident carrying `raw.Status === 'Cancelled'` (the
  // scoreline does NOT revert on the wire, so we read the Status, not a drop).
  // Baseball has no `goal` category, so this stays inert there.
  const annulledSide = useMemo(
    () => (isBaseball ? null : deriveAnnulledSide(frames)),
    [isBaseball, frames],
  );
  const annulledTeam =
    annulledSide === 'home'
      ? selectedMatch?.homeTeam
      : annulledSide === 'away'
        ? selectedMatch?.awayTeam
        : null;

  const setCategories = useCallback((categories: MatchEventCategory[]) => {
    setFilter((f) => ({ ...f, categories }));
  }, []);

  // URL-provided categories captured once at mount — the deep-link intent.
  const urlCategoriesRef = useRef<MatchEventCategory[] | null>(null);
  const urlCategoriesConsumed = useRef(false);
  const urlCategoriesInit = useRef(false);
  if (!urlCategoriesInit.current) {
    urlCategoriesInit.current = true;
    const c = searchParams.getAll('evCat') as MatchEventCategory[];
    urlCategoriesRef.current = c.length > 0 ? c : null;
  }

  // Reset the checked categories to the sport's default-on set when the resolved
  // sport changes. A deep-link's URL categories are honoured once (intersected
  // with the sport vocabulary; empty intersection → sport defaults), then the
  // intent is consumed so later fixture/sport switches fall to sport defaults.
  const prevSportRef = useRef<number | null | undefined>(undefined);
  useEffect(() => {
    const prev = prevSportRef.current;
    if (prev === sport) return;
    prevSportRef.current = sport;
    const vocab = new Set(categoriesForSport(sport));
    const urlCats = urlCategoriesRef.current;
    if (urlCats && !urlCategoriesConsumed.current) {
      if (sport != null) urlCategoriesConsumed.current = true;
      const intersect = urlCats.filter((c) => vocab.has(c));
      setCategories(
        intersect.length > 0 ? intersect : defaultCategoriesForSport(sport),
      );
      return;
    }
    // Provisional (null) → concrete resolution of the SAME fixture's sport:
    // keep the operator's current selection, dropping only categories the
    // resolved sport doesn't have (empty → sport defaults). A genuine switch
    // between two concrete sports still resets to the new sport's defaults.
    if (prev === null && sport != null) {
      setFilter((f) => {
        const kept = f.categories.filter((c) => vocab.has(c));
        return {
          ...f,
          categories:
            kept.length > 0 ? kept : defaultCategoriesForSport(sport),
        };
      });
      return;
    }
    setCategories(defaultCategoriesForSport(sport));
  }, [sport, setCategories, setFilter]);

  const toggleActive = useCallback(
    (fixtureId: number, next: boolean) => {
      if (next) activate.mutate(fixtureId);
      else deactivate.mutate(fixtureId);
    },
    [activate, deactivate],
  );

  return (
    <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2, flexShrink: 0 }}>
        <Typography variant="h5" sx={{ color: 'common.white' }}>
          Live match events
        </Typography>
        <Chip
          size="small"
          color={isConnected ? 'success' : 'default'}
          label={isConnected ? 'live' : 'disconnected'}
        />
        {sportMismatch && (
          <Chip
            size="small"
            label="⚠️ El feed indica otro deporte — se muestra según el feed"
            sx={{ bgcolor: '#ed6c02', color: '#fff', fontWeight: 700, height: 24 }}
          />
        )}

        {/* Soccer scoreboard — goals + corners tally per team (from the feed). */}
        {selectedMatch &&
          soccerSummary &&
          (soccerSummary.goals || soccerSummary.corners) && (
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ ml: 1, flexWrap: 'wrap' }}
              useFlexGap
            >
              {soccerSummary.goals && (
                <Typography variant="subtitle1" sx={{ color: 'common.white' }}>
                  ⚽ {selectedMatch.homeTeam}{' '}
                  <b>
                    {soccerSummary.goals.total.home}–
                    {soccerSummary.goals.total.away}
                  </b>{' '}
                  {selectedMatch.awayTeam}
                  {soccerSummary.goals.byHalf.length > 1 && (
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: 0.75 }}
                    >
                      ({formatHalfBreakdown(soccerSummary.goals)})
                    </Typography>
                  )}
                </Typography>
              )}
              {soccerSummary.corners && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={
                    `🚩 Corners ${soccerSummary.corners.total.home}–${soccerSummary.corners.total.away}` +
                    (soccerSummary.corners.byHalf.length > 1
                      ? ` (${formatHalfBreakdown(soccerSummary.corners)})`
                      : '')
                  }
                />
              )}
              {annulledTeam && (
                <Chip
                  size="small"
                  label={`⚠️ Gol anulado (${annulledTeam})`}
                  sx={{
                    bgcolor: '#d32f2f',
                    color: '#fff',
                    fontWeight: 700,
                    height: 24,
                  }}
                />
              )}
            </Stack>
          )}

        {/* Baseball scoreboard — running runs (home–away) + current inning. */}
        {selectedMatch &&
          baseballSummary &&
          (baseballSummary.runs ||
            baseballSummary.inning ||
            baseballSummary.final) && (
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ ml: 1, flexWrap: 'wrap' }}
              useFlexGap
            >
              {baseballSummary.runs && (
                <Typography variant="subtitle1" sx={{ color: 'common.white' }}>
                  ⚾ {selectedMatch.homeTeam}{' '}
                  <b>
                    {baseballSummary.runs.home}–{baseballSummary.runs.away}
                  </b>{' '}
                  {selectedMatch.awayTeam}
                </Typography>
              )}
              {baseballSummary.inning && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={`⚾ ${baseballSummary.inning}`}
                />
              )}
              {baseballSummary.final && (
                <Chip
                  size="small"
                  label="🏁 Final"
                  sx={{ bgcolor: '#9c27b0', color: '#fff', fontWeight: 700, height: 24 }}
                />
              )}
            </Stack>
          )}

        <Box sx={{ flex: 1 }} />
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={soundsEnabled}
              onChange={(e) => setSoundsEnabled(e.target.checked)}
              inputProps={{ 'aria-label': 'goal and corner sounds' }}
            />
          }
          label="🔊 Sounds"
        />
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, mb: 2, flexShrink: 0 }}>
        <Stack spacing={2}>
          {/* Match picker + per-match activate/deactivate toggles. */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ md: 'center' }}
          >
            <FormControl size="small" sx={{ flex: 1, minWidth: 260 }}>
              <InputLabel id="match-picker">Match</InputLabel>
              <Select
                labelId="match-picker"
                label="Match"
                value={filter.fixtureId != null ? String(filter.fixtureId) : ''}
                onChange={(e) =>
                  setFilter((f) => ({
                    ...f,
                    fixtureId: e.target.value ? Number(e.target.value) : null,
                  }))
                }
              >
                <MenuItem value="">
                  <em>— select a match —</em>
                </MenuItem>
                {matches.map((m) => (
                  <MenuItem key={m.fixtureId} value={String(m.fixtureId)}>
                    {m.homeTeam} vs {m.awayTeam}
                    {m.league ? ` · ${m.league}` : ''}
                    {/* `live` = runtime feed status (Redis); `active` = the
                        persisted operator intent (Mongo, the Switch below). */}
                    {m.live ? ' · live' : m.active ? ' · activating…' : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedMatch && (
              <FormControlLabel
                control={
                  <Switch
                    checked={selectedMatch.active}
                    onChange={(e) =>
                      toggleActive(selectedMatch.fixtureId, e.target.checked)
                    }
                    inputProps={{ 'aria-label': 'activate match feed' }}
                  />
                }
                label={selectedMatch.active ? 'Active' : 'Inactive'}
              />
            )}
          </Stack>

          <Divider flexItem />

          {/* Add a match by LSports fixture ID → appears in the picker above. */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            alignItems={{ md: 'center' }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ minWidth: 96 }}
            >
              Add match
            </Typography>
            <TextField
              size="small"
              label="Fixture ID"
              value={newMatch.fixtureId}
              onChange={(e) =>
                setNewMatch((m) => ({
                  ...m,
                  fixtureId: e.target.value.replace(/[^0-9]/g, ''),
                }))
              }
              inputProps={{ inputMode: 'numeric' }}
              sx={{ width: 130 }}
            />
            <TextField
              size="small"
              label="Home team"
              value={newMatch.homeTeam}
              onChange={(e) =>
                setNewMatch((m) => ({ ...m, homeTeam: e.target.value }))
              }
              sx={{ flex: 1, minWidth: 150 }}
            />
            <TextField
              size="small"
              label="Away team"
              value={newMatch.awayTeam}
              onChange={(e) =>
                setNewMatch((m) => ({ ...m, awayTeam: e.target.value }))
              }
              sx={{ flex: 1, minWidth: 150 }}
            />
            <TextField
              size="small"
              label="League (optional)"
              value={newMatch.league}
              onChange={(e) =>
                setNewMatch((m) => ({ ...m, league: e.target.value }))
              }
              sx={{ flex: 1, minWidth: 140 }}
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel id="add-match-sport">Sport</InputLabel>
              <Select
                labelId="add-match-sport"
                label="Sport"
                value={newMatch.sport}
                onChange={(e) =>
                  setNewMatch((m) => ({
                    ...m,
                    sport: e.target.value as 'auto' | 'soccer' | 'baseball',
                  }))
                }
              >
                <MenuItem value="auto">Auto</MenuItem>
                <MenuItem value="soccer">⚽ Soccer</MenuItem>
                <MenuItem value="baseball">⚾ Baseball</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="contained"
              size="small"
              onClick={addMatch}
              disabled={!canAddMatch || upsert.isLoading}
            >
              {upsert.isLoading ? 'Adding…' : 'Add'}
            </Button>
          </Stack>

          {/* Filter bar. */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ md: 'center' }}
          >
            <FormControl size="small" sx={{ flex: 1, minWidth: 240 }}>
              <InputLabel id="category-filter">Categories</InputLabel>
              <Select
                labelId="category-filter"
                multiple
                value={filter.categories}
                onChange={(e) =>
                  setCategories(
                    (typeof e.target.value === 'string'
                      ? e.target.value.split(',')
                      : e.target.value) as MatchEventCategory[],
                  )
                }
                input={<OutlinedInput label="Categories" />}
                renderValue={(selected) => (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {(selected as MatchEventCategory[]).map((c) => {
                      const d = getCategoryDisplay(c);
                      return (
                        <Chip
                          key={c}
                          size="small"
                          label={`${d.emoji} ${d.label}`}
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      );
                    })}
                  </Stack>
                )}
              >
                {sportCategories.map((c) => {
                  const d = getCategoryDisplay(c);
                  return (
                    <MenuItem key={c} value={c}>
                      {d.emoji} {d.label}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            <Box sx={{ flex: 1, minWidth: 180, px: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Min confidence: {Math.round(filter.minConfidence * 100)}%
              </Typography>
              <Slider
                size="small"
                min={0}
                max={1}
                step={0.05}
                value={filter.minConfidence}
                onChange={(_, v) =>
                  setFilter((f) => ({
                    ...f,
                    minConfidence: Array.isArray(v) ? v[0] : v,
                  }))
                }
                aria-label="minimum confidence"
              />
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={filter.showAll}
                  onChange={(e) =>
                    setFilter((f) => ({ ...f, showAll: e.target.checked }))
                  }
                  inputProps={{ 'aria-label': 'show all updates' }}
                />
              }
              label="Show all updates"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={filter.raw}
                  onChange={(e) =>
                    setFilter((f) => ({ ...f, raw: e.target.checked }))
                  }
                  inputProps={{ 'aria-label': 'raw all incidents' }}
                />
              }
              label="Raw / all incidents"
            />
          </Stack>
        </Stack>
      </Paper>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {isError && (
          <Alert severity="error" sx={{ mb: 1 }}>
            Failed to load matches.
          </Alert>
        )}
        {isLoading && (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={24} />
          </Stack>
        )}
        {!subscribeFixtureId && !isLoading && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            {selectedMatch && !selectedMatch.active
              ? 'Selected match is not active — activate it to stream live events.'
              : 'Select an active match to stream live events.'}
          </Typography>
        )}
        {subscribeFixtureId && rows.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            Waiting for live events…
          </Typography>
        )}
        {feedItems.map((item) =>
          item.type === 'marker' ? (
            <Divider
              key={item.key}
              textAlign="center"
              sx={{
                my: 1,
                '&::before, &::after': { borderColor: 'warning.main' },
              }}
            >
              <Chip
                size="small"
                label={`⏱️ ${item.label}`}
                sx={{
                  bgcolor: 'warning.main',
                  color: '#000',
                  fontWeight: 700,
                }}
              />
            </Divider>
          ) : (
            <MatchEventRow
              key={item.key}
              row={item.row}
              homeTeam={selectedMatch?.homeTeam}
              awayTeam={selectedMatch?.awayTeam}
            />
          ),
        )}
      </Box>
    </Box>
  );
}
